import React, { useState, useEffect } from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { Capacitor } from '@capacitor/core'
import { Share } from '@capacitor/share'
import { Filesystem, Directory } from '@capacitor/filesystem'
import {
	IonButton,
	IonIcon,
    useIonAlert,
} from '@ionic/react'
import {
    useEditModeState,
    useMySeason,
    useSection,
    useShowPositionPreferencesState,
} from './data'
import {
    getUrl,
} from './util'
import { shareOutline } from 'ionicons/icons'
import './sharebutton.css'
import { shareUrl } from './shareUrl'

const ENABLE_NATIVE_PRINTING_ON_WEB = false 		// SHOULD BE ENABLED FOR TESTING ONLY

const PDF_STYLE_ID 					= 'force-print-rules'
const PDF_HIDDEN_BOX_ID 			= 'print-hidden-box'

//
// This is a bit messy because it copies the URL to the Clipboard
// and THEN tries to use the Web Share API - which, if it succeeds,
// has no use for the Clipboard. So we overwrite the Clipboard
// unneccessarily in that situation. But I can't figure out how to
// do it otherwise.
//


type ShareButtonProps = {
	url?: string
	title?: string
	fill?: 'clear' | 'outline' | 'solid' | 'default'
	size?: 'small' | 'default' | 'large'
	expand?: 'full' | 'block'
	shareOnly?: boolean
}

const ShareButton = (props: React.PropsWithChildren<ShareButtonProps>) => {

    const [ presentAlert ] = useIonAlert()

    const [ haveShareAPI, setHaveShareAPI ] = useState(false)

    const [ editMode, setEditMode ] = useEditModeState()
    const [ showPositionPreferences, setShowPositionPreferences ] = useShowPositionPreferencesState()

    const section = useSection()

	const season = useMySeason()
	const privacy = !!season?.privacy

	const pageUrl = props.url || getUrl({ section })
	const pageTitle = props.title || document.title

    //
    // On mount, test whether we can use the Capacitor Share API
    //
    useEffect(() => {
        Share.canShare().then(result => setHaveShareAPI(result?.value))
    }, [ ])

	const prepareForPrint = async () => {
		if (showPositionPreferences) {
			setShowPositionPreferences(false)
		}
		if (editMode) {
			setEditMode(false)
		}
		// Give it time to take effect
		await new Promise(r => setTimeout(r, 100))
	}

	const onNativeShare = () => {
		console.log('Sharing via Native Share API')

		void shareUrl({
			url: pageUrl,
			title: pageTitle,
			presentAlert,
		}).catch(error => {
			console.error('Failed to share URL', error)
		})
	}

	const onNativePrint	= async () => {

		await prepareForPrint()

		const container = document.createElement('div')
		container.id = 'board' 		// Because I have lots of '#board' CSS rules
		container.className = 'print-pdf'
		container.style.backgroundColor = '#ffffff'

		// TitleBar can't be cloned like the rest because of some Shadow DOM bullshit
		const titleBar = document.querySelector('ion-header ion-title .is-title')?.parentElement
		if (titleBar) {
			const titleEl = document.createElement('div')
			titleEl.className = 'toolbar-title'
			titleEl.innerHTML = titleBar.innerHTML
			container.appendChild(titleEl)
		}

		const elements = [
			// "vs Opponent / Round X"
			document.querySelector('.header-title'),

			// Main content
			document.querySelector('.roster-container'),
		]

		elements.forEach((el, index) => {
			if (!el) {
				console.error("Failed to load all elements!")
				console.log("Failed to load element #", index, el)
				return
			}
			container.appendChild(el.cloneNode(true))
		})

		//
		// Create an outer container that can have the '#print-hidden-box'
		// ID, which is used to inject print styles.
		//
		const outerContainer = document.createElement('div')
		outerContainer.id = PDF_HIDDEN_BOX_ID
		outerContainer.style.backgroundColor = '#ffffff'
		outerContainer.appendChild(container)

		//
		// Hide all this from the user to avoid a brief, ugly flash of
		// print-style formatting on their screen.
		//
		const hiddenBox = document.createElement('div')
		hiddenBox.style.position = 'absolute'
		hiddenBox.style.left = '-9999px'
		hiddenBox.appendChild(outerContainer)

		document.body.appendChild(hiddenBox)

		console.log("Appended")
		await new Promise(r => setTimeout(r, 100))

		injectPrintStylesAsScreen()
		console.log("Injected print styles")
		await new Promise(r => setTimeout(r, 100))

		const html2pdf = (await import('html2pdf.js')).default

		const opt = {
			margin:       0.6,
			filename:     'AutoRoster.pdf',
			image:        { type: 'png' as const },
			html2canvas:  { scale: 2, backgroundColor: '#ffffff' },
			jsPDF: { unit: 'in' as const, format: 'a4' as const, orientation: 'landscape' as const }
		}

		const pdfBlob = await html2pdf().set(opt).from(outerContainer).outputPdf('blob')

		removeInjectedPrintStyles()

		hiddenBox.remove()

		const base64 = await blobToBase64(pdfBlob)

		try {
			if (Capacitor.isNativePlatform()) {
				await sharePDF(base64)
				console.log("Yay - successfully invoked sharePDF()")
			} else {
				//
				// For web, we create a download link. This code is only accessible
				// with ENABLE_NATIVE_PRINTING_ON_WEB set to true.
				//
				const url = URL.createObjectURL(pdfBlob)
				const a = document.createElement('a')
				a.href = url
				a.download = 'AutoRoster.pdf'
				a.click()
				URL.revokeObjectURL(url)
			}
		} catch (err) {
			console.error("onNativePrint() failed: " + err)
		}
	}

	const onShare = () => {

		const isRosterPage = !props.shareOnly && window.location.pathname.substring(0, 7) === '/games/'
		// console.log('share!', haveShareAPI, isRosterPage)

		//
		// On Roster pages, you can print or share. But elsewhere you
		// can only share.
		//
		// Note: window.print() doesn't work on native devices. You need to
		// create a PDF (with difficulty) instead.
		//
		if (isRosterPage) {
			presentAlert({
				header: 'Print or Share Roster',
				buttons: [
					{
						text: 'Print',
						handler: () => {
							if (Capacitor.isNativePlatform() || ENABLE_NATIVE_PRINTING_ON_WEB) {
								onNativePrint()
							} else {
								onWebPrint()
							}
						}
					},
					{
						text: 'Share',
						handler: () => {
							if (Capacitor.isNativePlatform() && haveShareAPI) {
								onNativeShare()
							} else {
								setTimeout(onWebShare, 800)
							}
						},
					},
				],
			})
		} else {
			// No printing on non-Roster pages, only sharing.
			if (Capacitor.isNativePlatform()) {
				onNativeShare()
			} else {
				onWebShare()
			}
		}
	}

	const onWebShare = () => {
		console.log('Sharing via web share flow', pageUrl)
		void shareUrl({
			url: pageUrl,
			title: pageTitle,
			presentAlert,
		}).catch(error => {
			console.error('Failed to share URL', error)
		})
	}

	const onWebPrint = async () => {

		await prepareForPrint()

		//
		// For some reason, iOS Safari can insert a REALLY long delay
		// after hitting "Print" before it shows the print preview
		// screen... like 30s. So we try execCommand() first, which only
		// works on Safari (in 2025) and is instant.
		//
		// We need a small delay to allow the "Print" dialog to close.
		// We don't want to print that!
		//
		setTimeout(() => {
			console.log("LET US PRINT")
			let printed = false

			try {
				console.log('execCommand print')
				printed = document.execCommand('print', false)
			}
			catch(e) {
				console.log("execCommand print failed")
			}

			if (!printed) {
				console.log("window.print fallback")
				window.print()
			}
		}, 100)
	}

	return (
		<CopyToClipboard text={pageUrl}>

			<IonButton
				className="button-share"
				size={props.size || "small"}
				disabled={privacy}
				onClick={onShare}
				title="Share"
				slot="primary"
				fill={props.fill || "clear"}
				expand={props.expand}
			>
				<IonIcon
					icon={shareOutline}
					slot={props.children ? 'start' : 'end'}
				/>
				{props.children}
			</IonButton>

		</CopyToClipboard>
	)
}

//
// Copy all '@media only print' rules from CSS into the PDF
//
function injectPrintStylesAsScreen() {

	const printRules = [ ]

	for (const sheet of Array.from(document.styleSheets)) {
		try {
			for (const rule of Array.from(sheet.cssRules || [])) {
				if (rule instanceof CSSMediaRule && /print/i.test(rule.media.mediaText)) {
					for (const subRule of Array.from(rule.cssRules)) {
						printRules.push(subRule.cssText)
					}
				}
			}
		} catch (e) {
			console.warn('Skipping stylesheet (possibly cross-origin)', sheet.href)
		}
	}

	const style = document.createElement('style')
	style.id = PDF_STYLE_ID
	style.textContent = printRules.map(css => css.replace(/(^|\})\s*([^{]+)\s*\{/g, (_, boundary, selectors) =>
														  `${boundary} ${selectors
															  .split(',')
															  .map((sel: string) => `#${PDF_HIDDEN_BOX_ID} ${sel.trim()}`)
															  .join(', ')} {`
														 )).join('\n')
	document.head.appendChild(style)
}

function removeInjectedPrintStyles() {
	const style = document.getElementById(PDF_STYLE_ID)
	style?.remove()
}

async function sharePDF(base64: string) {

	const { uri } = await Filesystem.writeFile({
		path: 'AutoRoster.pdf',
		data: base64,
		directory: Directory.Documents,
	})

	await Share.share({
		title: 'Roster',
		url: uri,
	})
}

// Converts Blob → base64
async function blobToBase64(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.onloadend = () => {
			const result = reader.result as string
			const base64 = result.split(',')[1]
			resolve(base64)
		}
		reader.onerror = reject
		reader.readAsDataURL(blob)
	})
}

export default ShareButton
